import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Hourglass, Plus, Edit2, Trash2, Loader2, ShieldCheck, Tag } from "lucide-react";

const VEHICLE_TYPES = ["sedan", "suv", "4x4", "minibus", "pickup"];
const VEHICLE_TYPE_LABELS = { sedan: "Berline", suv: "SUV", "4x4": "4x4", minibus: "Minibus", pickup: "Pickup" };
const PACK_COLORS = { M: "#10B981", L: "#3B82F6", XL: "#F59E0B" };

function emptyForm(type = "") {
  return {
    type: type.toUpperCase(),
    label: type ? `Pack ${type.toUpperCase()}` : "",
    hours: 5, price_xaf: 25000, rate_per_hour: 5000, validity_days: 3,
    filter_kind: "any", filter_types: [], filter_max_price: 10000,
    description: "", active: true,
  };
}

function buildVehicleFilter(form) {
  if (form.filter_kind === "types") return { kind: "types", vehicle_types: form.filter_types };
  if (form.filter_kind === "price_max") return { kind: "price_max", max_price_per_hour: Number(form.filter_max_price || 0) };
  return { kind: "any" };
}

function packToForm(p) {
  const vf = p.vehicle_filter || { kind: "any" };
  return {
    type: p.type, label: p.label || "", hours: p.hours, price_xaf: p.price_xaf, rate_per_hour: p.rate_per_hour,
    validity_days: p.validity_days, filter_kind: vf.kind || "any",
    filter_types: vf.vehicle_types || [], filter_max_price: vf.max_price_per_hour || 10000,
    description: p.description || "", active: p.active !== false,
  };
}

function describeFilter(vf) {
  if (!vf || vf.kind === "any") return "Tous les véhicules";
  if (vf.kind === "types") return (vf.vehicle_types || []).map((t) => VEHICLE_TYPE_LABELS[t] || t).join(", ") || "—";
  if (vf.kind === "price_max") return `≤ ${Number(vf.max_price_per_hour || 0).toLocaleString()} F/h`;
  return "—";
}

export default function AdminPackCatalog() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: "create" | "edit", form }
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pack-catalog");
      setPacks(data.packs || []);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDialog({ mode: "create", form: emptyForm() });
  const openEdit = (p) => setDialog({ mode: "edit", form: packToForm(p) });

  const handleSave = async () => {
    if (!dialog) return;
    const f = dialog.form;
    if (!f.type) { toast.error("Type pack requis"); return; }
    setSaving(true);
    try {
      const body = {
        type: f.type, label: f.label, hours: Number(f.hours), price_xaf: Number(f.price_xaf),
        rate_per_hour: Number(f.rate_per_hour), validity_days: Number(f.validity_days),
        vehicle_filter: buildVehicleFilter(f), description: f.description, active: f.active,
      };
      if (dialog.mode === "create") {
        await api.post("/admin/pack-catalog", body);
        toast.success(`Pack ${f.type} créé`);
      } else {
        await api.put(`/admin/pack-catalog/${f.type}`, body);
        toast.success(`Pack ${f.type} mis à jour`);
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.error("Échec enregistrement", { description: e.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const handleToggle = async (p) => {
    try {
      await api.patch(`/admin/pack-catalog/${p.type}/active`);
      toast.success(`Pack ${p.type} ${p.active ? "désactivé" : "activé"}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/pack-catalog/${confirmDelete.type}`);
      toast.success(`Pack ${confirmDelete.type} supprimé`);
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  return (
    <div className="space-y-5" data-testid="admin-pack-catalog-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Tag className="w-5 h-5 text-[#FACC15]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Catalogue des Pack Heures</h2>
          </div>
          <p className="text-sm text-white/70">Créez, modifiez et activez/désactivez les packs sans redéploiement. Les packs déjà achetés conservent leur tarif d&apos;origine.</p>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-bold" data-testid="create-pack-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau pack
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#0A2540] animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {packs.map((p) => {
            const color = PACK_COLORS[p.type] || "#0A2540";
            return (
              <div key={p.type} className={`bg-white rounded-2xl border-2 overflow-hidden ${p.active ? "border-[#10B981]/40" : "border-[#94A3B8]/40 opacity-70"}`} data-testid={`pack-card-${p.type}`}>
                <div className="h-1.5" style={{ background: color }} />
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{p.label || `Pack ${p.type}`}</h3>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.active ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#94A3B8]/20 text-[#64748B]"}`}>
                          {p.active ? "Actif" : "Désactivé"}
                        </span>
                      </div>
                      <p className="text-[10px] text-[#64748B] mt-0.5">Type : <code className="font-mono">{p.type}</code></p>
                    </div>
                    <Switch checked={p.active} onCheckedChange={() => handleToggle(p)} data-testid={`toggle-pack-${p.type}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-[#F8FAFC] rounded-lg p-2"><p className="text-[10px] text-[#64748B] uppercase">Heures</p><p className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`pack-hours-${p.type}`}>{p.hours} h</p></div>
                    <div className="bg-[#F8FAFC] rounded-lg p-2"><p className="text-[10px] text-[#64748B] uppercase">Prix</p><p className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`pack-price-${p.type}`}>{p.price_xaf.toLocaleString()} F</p></div>
                    <div className="bg-[#F8FAFC] rounded-lg p-2"><p className="text-[10px] text-[#64748B] uppercase">Tarif unitaire</p><p className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`pack-rate-${p.type}`}>{p.rate_per_hour.toLocaleString()} F/h</p></div>
                    <div className="bg-[#F8FAFC] rounded-lg p-2"><p className="text-[10px] text-[#64748B] uppercase">Validité</p><p className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`pack-validity-${p.type}`}>{p.validity_days} j</p></div>
                  </div>
                  <div className="text-[11px] text-[#64748B] bg-[#FACC15]/10 rounded-lg p-2 border border-[#FACC15]/30">
                    <strong>Éligibilité :</strong> {describeFilter(p.vehicle_filter)}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openEdit(p)} size="sm" variant="outline" className="flex-1 text-xs" data-testid={`edit-pack-${p.type}`}><Edit2 className="w-3 h-3 mr-1" /> Modifier</Button>
                    <Button onClick={() => setConfirmDelete(p)} size="sm" variant="outline" className="text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/5 text-xs" data-testid={`delete-pack-${p.type}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pack-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <ShieldCheck className="w-4 h-4 text-[#FACC15]" />
              {dialog?.mode === "create" ? "Créer un pack" : `Modifier le pack ${dialog?.form?.type}`}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type (ID)</Label>
                  <Input value={dialog.form.type} onChange={(e) => setF({ type: e.target.value.toUpperCase().slice(0, 10) })} placeholder="M / L / XL / XXL..." disabled={dialog.mode === "edit"} className="h-9 mt-1 font-mono" data-testid="form-pack-type" />
                </div>
                <div>
                  <Label className="text-xs">Label affiché</Label>
                  <Input value={dialog.form.label} onChange={(e) => setF({ label: e.target.value })} placeholder="Pack X" className="h-9 mt-1" data-testid="form-pack-label" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Heures incluses</Label><Input type="number" min={1} value={dialog.form.hours} onChange={(e) => setF({ hours: e.target.value })} className="h-9 mt-1" data-testid="form-pack-hours" /></div>
                <div><Label className="text-xs">Validité (jours)</Label><Input type="number" min={1} value={dialog.form.validity_days} onChange={(e) => setF({ validity_days: e.target.value })} className="h-9 mt-1" data-testid="form-pack-validity" /></div>
                <div><Label className="text-xs">Prix total (XAF)</Label><Input type="number" min={1} value={dialog.form.price_xaf} onChange={(e) => setF({ price_xaf: e.target.value })} className="h-9 mt-1" data-testid="form-pack-price" /></div>
                <div><Label className="text-xs">Tarif unitaire (F/h)</Label><Input type="number" min={1} value={dialog.form.rate_per_hour} onChange={(e) => setF({ rate_per_hour: e.target.value })} className="h-9 mt-1" data-testid="form-pack-rate" /></div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={dialog.form.description} onChange={(e) => setF({ description: e.target.value })} placeholder="Berlines éco, etc." className="h-9 mt-1" data-testid="form-pack-description" />
              </div>
              {/* Vehicle filter */}
              <div className="bg-[#F8FAFC] rounded-lg p-3 space-y-2">
                <Label className="text-xs font-semibold text-[#0A2540]">Éligibilité véhicules</Label>
                <select className="h-9 w-full border border-[#CBD5E1] rounded-md px-2 text-sm bg-white" value={dialog.form.filter_kind} onChange={(e) => setF({ filter_kind: e.target.value })} data-testid="form-filter-kind">
                  <option value="any">Tous les véhicules</option>
                  <option value="types">Types spécifiques</option>
                  <option value="price_max">Prix unitaire ≤ X F/h</option>
                </select>
                {dialog.form.filter_kind === "types" && (
                  <div className="flex flex-wrap gap-1">
                    {VEHICLE_TYPES.map((t) => {
                      const checked = dialog.form.filter_types.includes(t);
                      return (
                        <button key={t} type="button" onClick={() => setF({ filter_types: checked ? dialog.form.filter_types.filter((x) => x !== t) : [...dialog.form.filter_types, t] })} className={`text-[11px] px-2 py-1 rounded-full border ${checked ? "bg-[#FACC15] border-[#FACC15] text-[#0A2540] font-bold" : "border-[#CBD5E1] text-[#64748B]"}`} data-testid={`form-filter-type-${t}`}>{VEHICLE_TYPE_LABELS[t]}</button>
                      );
                    })}
                  </div>
                )}
                {dialog.form.filter_kind === "price_max" && (
                  <div>
                    <Label className="text-[10px]">Prix unitaire max (F/h)</Label>
                    <Input type="number" min={1} value={dialog.form.filter_max_price} onChange={(e) => setF({ filter_max_price: e.target.value })} className="h-9 mt-0.5" data-testid="form-filter-max-price" />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-3">
                <Label className="text-xs">Pack actif (visible par les passagers)</Label>
                <Switch checked={dialog.form.active} onCheckedChange={(v) => setF({ active: v })} data-testid="form-pack-active" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialog(null)} className="flex-1" data-testid="form-cancel-btn">Annuler</Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="form-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="confirm-delete-dialog">
          <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Supprimer le pack ?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B]">Cette action est irréversible. Préférez la désactivation pour conserver l&apos;historique.</p>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1">Annuler</Button>
            <Button onClick={handleDelete} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-delete-btn">Supprimer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
